import type { JSX } from "react";
import { Link, useLocation } from "react-router";

import { useMeQuery } from "../../lib/api/auth.js";
import { cn } from "../../lib/cn.js";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet.js";

import { BrandMark } from "./BrandMark.js";
import { activeNavTo, visibleNavSections } from "./nav-config.js";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Off-canvas navigation drawer shown on viewports below the `md` breakpoint
 * in place of the desktop icon rail (`AppSidebar`). Uses the same
 * `navSections` source so the two stay in sync; closes on item click.
 */
export function MobileNavDrawer({ open, onOpenChange }: Props): JSX.Element {
  const location = useLocation();
  const isAdmin = useMeQuery().data?.role === "tenant_admin";
  const navSections = visibleNavSections(isAdmin);
  const allNavItems = navSections.flatMap((section) => section.items);
  const activeTo = activeNavTo(location.pathname, allNavItems);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-[18rem] max-w-[85vw] gap-0 border-r-0 bg-sidebar p-0 text-sidebar-foreground"
      >
        <SheetHeader className="border-b border-sidebar-border px-4 py-4">
          <SheetTitle className="flex items-center gap-2 text-sidebar-foreground">
            <BrandMark className="h-7 w-7" />
            <span className="font-brand">Onshelf</span>
          </SheetTitle>
        </SheetHeader>
        <nav className="flex-1 overflow-y-auto py-2">
          {navSections.map((section, idx) => (
            <div key={section.group}>
              {idx > 0 ? (
                <div className="mx-3 my-2 border-t border-sidebar-border" />
              ) : null}
              <ul className="flex flex-col gap-1 px-2">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = item.to === activeTo;
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        aria-current={active ? "page" : undefined}
                        onClick={() => onOpenChange(false)}
                        className={cn(
                          "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                          active
                            ? "bg-sidebar-accent text-sidebar-foreground"
                            : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60",
                        )}
                      >
                        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
        <div className="border-t border-sidebar-border px-4 py-3 text-[10px] text-sidebar-foreground/40">
          v0.0.0
        </div>
      </SheetContent>
    </Sheet>
  );
}
